/**
 * Per-rule security-standard map — the dashboard-local projection of the
 * canonical table in `docs/security/framework-mappings.md` (section
 * "Policy rules" + "Non-policy controls"). Each catalog rule maps to a
 * list of standards (framework + id + optional display label); the Add-
 * rule popup finder uses this list to answer queries like "OWASP",
 * "LLM01", "MITRE ATLAS", or "AML.T0139" by surfacing every rule the
 * canon maps to that framework.
 *
 * The canonical doc is the single source of truth: a drift test
 * (`__tests__/rule-standards-drift.test.ts`) parses the "Policy rules"
 * table and fails if any rule's map diverges from the published mapping.
 * So edits to this file MUST be accompanied by matching edits to
 * `docs/security/framework-mappings.md`, and vice-versa.
 *
 * Catalog `reference` strings (AP2 §, UK CMA, UK DMCC, CVE, …) are
 * folded into the per-rule list too, as a second standards family that
 * sits alongside the four public frameworks. The finder matches on
 * those as well so "AP2" surfaces every rule carrying an AP2 citation,
 * and "DMCC" surfaces r09.
 *
 * Non-policy controls (r11 / r16 / r21 / r24 / r26 / r27 / r29) are
 * included — the finder is for a merchant who wants to find a rule by
 * standard, not for the publish gate, so hiding always-on rules here
 * would make "LLM02" miss r21.
 */

import type { RuleType } from '@sill/types';

export type StandardFramework =
  | 'OWASP LLM'
  | 'OWASP Agentic'
  | 'MITRE ATLAS'
  | 'NIST AI RMF'
  | 'AP2'
  | 'OWASP API'
  | 'OWASP MCP'
  | 'UK CMA'
  | 'UK DMCC'
  | 'FTC'
  | 'CVE'
  | 'Goodside'
  | 'GDM';

export interface StandardRef {
  readonly framework: StandardFramework;
  /** Canonical id as it appears in the published mapping. Null for
   *  tactic-only citations ("Initial Access", "Impact") that don't carry
   *  a MITRE AML.T####-shaped technique id. */
  readonly id: string | null;
  /** Short display string the finder renders in "Matches: …" lines and
   *  on the Standards row inside the rule detail / edit pane. */
  readonly label: string;
}

/**
 * Framework alias table — merchant-plain query terms that resolve to a
 * framework name. Keys are lower-cased before match; values are the
 * canonical `StandardFramework` entry. "owasp" matches every OWASP
 * family; "mitre" and "atlas" both resolve to MITRE ATLAS.
 */
export const FRAMEWORK_ALIASES: Readonly<Record<string, readonly StandardFramework[]>> = {
  owasp: ['OWASP LLM', 'OWASP Agentic', 'OWASP API', 'OWASP MCP'],
  'owasp llm': ['OWASP LLM'],
  llm: ['OWASP LLM'],
  'owasp agentic': ['OWASP Agentic'],
  agentic: ['OWASP Agentic'],
  'owasp api': ['OWASP API'],
  api: ['OWASP API'],
  'owasp mcp': ['OWASP MCP'],
  mcp: ['OWASP MCP'],
  mitre: ['MITRE ATLAS'],
  atlas: ['MITRE ATLAS'],
  'mitre atlas': ['MITRE ATLAS'],
  aml: ['MITRE ATLAS'],
  nist: ['NIST AI RMF'],
  'nist ai rmf': ['NIST AI RMF'],
  ap2: ['AP2'],
  cma: ['UK CMA'],
  'uk cma': ['UK CMA'],
  dmcc: ['UK DMCC'],
  'uk dmcc': ['UK DMCC'],
  ftc: ['FTC'],
  cve: ['CVE'],
  goodside: ['Goodside'],
  gdm: ['GDM'],
};

/** "OWASP MCP" + "MCP02" reads "OWASP MCP02", not "OWASP MCP MCP02". */
function standardLabel(framework: StandardFramework, id: string | null): string {
  if (id === null) return framework;
  const family = framework.split(' ').pop() ?? '';
  if (framework.startsWith('OWASP ') && id.toUpperCase().startsWith(family.toUpperCase())) {
    return `OWASP ${id}`;
  }
  return `${framework} ${id}`;
}

const r = (framework: StandardFramework, id: string | null, label?: string): StandardRef => ({
  framework,
  id,
  label: label ?? standardLabel(framework, id),
});

/**
 * Rule → standards map. Transcribed from
 * `docs/security/framework-mappings.md` "Policy rules" + "Non-policy
 * controls" sections; cells like "AML.T0051, AML.T0054, AML.T0018.003
 * (all partial)" are split into one entry per id. Tactic-only citations
 * ("Impact (tactic)", "Initial Access (tactic)") carry `id: null`.
 *
 * The drift test parses the markdown rows (keyed by the "Rule" column)
 * and fails if a rule's list of OWASP LLM / OWASP Agentic / MITRE ATLAS
 * ids here disagrees with the published row.
 */
export const RULE_STANDARDS: Readonly<Partial<Record<RuleType, readonly StandardRef[]>>> = {
  // -- Policy rules --
  r01: [
    r('OWASP Agentic', 'ASI03'),
    r('OWASP Agentic', 'ASI10'),
    r('MITRE ATLAS', null, 'MITRE ATLAS Initial Access'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
    r('AP2', '§4'),
  ],
  r02: [
    r('OWASP LLM', 'LLM06'),
    r('OWASP Agentic', 'ASI01'),
    r('OWASP Agentic', 'ASI03'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
    r('AP2', '§4'),
  ],
  r03: [
    r('OWASP LLM', 'LLM10'),
    r('OWASP Agentic', 'ASI08'),
    r('MITRE ATLAS', null, 'MITRE ATLAS Impact'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
    r('OWASP API', 'API4'),
  ],
  r04: [
    r('OWASP LLM', 'LLM10'),
    r('OWASP Agentic', 'ASI08'),
    r('MITRE ATLAS', null, 'MITRE ATLAS Impact'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
  ],
  r05: [
    r('OWASP LLM', 'LLM06'),
    r('OWASP LLM', 'LLM10'),
    r('OWASP Agentic', 'ASI01'),
    r('MITRE ATLAS', null, 'MITRE ATLAS Impact'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
    r('AP2', '§5'),
  ],
  r06: [
    r('OWASP LLM', 'LLM06'),
    r('OWASP LLM', 'LLM10'),
    r('OWASP Agentic', 'ASI08'),
    r('MITRE ATLAS', null, 'MITRE ATLAS Impact'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
    r('OWASP API', 'API4:2023'),
  ],
  r07: [
    r('OWASP LLM', 'LLM06'),
    r('OWASP Agentic', 'ASI08'),
    r('OWASP Agentic', 'ASI09'),
    r('MITRE ATLAS', null, 'MITRE ATLAS Impact'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
  ],
  r08: [
    r('OWASP Agentic', 'ASI09', 'OWASP Agentic ASI09 (partial)'),
    r('UK CMA', null, 'UK CMA'),
    r('FTC', null, 'FTC 16 CFR 464'),
  ],
  r09: [
    r('OWASP Agentic', 'ASI09', 'OWASP Agentic ASI09 (partial)'),
    r('UK DMCC', null, 'UK DMCC Act 2024'),
  ],
  r10: [
    r('OWASP LLM', 'LLM01'),
    r('OWASP Agentic', 'ASI01'),
    r('MITRE ATLAS', 'AML.T0051', 'MITRE ATLAS AML.T0051 (partial)'),
    r('MITRE ATLAS', 'AML.T0054', 'MITRE ATLAS AML.T0054 (partial)'),
    r('MITRE ATLAS', 'AML.T0018.003', 'MITRE ATLAS AML.T0018.003 (partial)'),
    r('NIST AI RMF', null, 'NIST AI RMF Measure'),
  ],
  r11: [
    r('OWASP Agentic', 'ASI03'),
    r('MITRE ATLAS', 'AML.T0091.001', 'MITRE ATLAS AML.T0091.001 (partial)'),
    r('MITRE ATLAS', null, 'MITRE ATLAS Evasion'),
    r('NIST AI RMF', null, 'NIST AI RMF Measure'),
    r('AP2', '§6.1'),
  ],
  r12: [
    r('MITRE ATLAS', null, 'MITRE ATLAS Initial Access'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
    r('OWASP API', 'API8:2023'),
  ],
  r13: [
    r('OWASP LLM', 'LLM10'),
    r('OWASP Agentic', 'ASI08'),
    r('MITRE ATLAS', null, 'MITRE ATLAS Impact'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
  ],
  r14: [
    r('OWASP LLM', 'LLM06'),
    r('OWASP Agentic', 'ASI01'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
    r('AP2', '§5.2'),
  ],
  r15: [
    r('OWASP LLM', 'LLM06'),
    r('OWASP Agentic', 'ASI01'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
    r('AP2', '§5.2'),
  ],
  r16: [
    r('OWASP LLM', 'LLM06'),
    r('OWASP Agentic', 'ASI01'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
  ],
  r17: [
    r('OWASP LLM', 'LLM06'),
    r('OWASP Agentic', 'ASI03'),
    r('MITRE ATLAS', null, 'MITRE ATLAS Exfiltration'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
    r('OWASP API', 'API1', 'OWASP API1 (BOLA)'),
  ],
  r18: [
    r('OWASP LLM', 'LLM03', 'OWASP LLM03 (partial)'),
    r('OWASP Agentic', 'ASI02', 'OWASP Agentic ASI02 (partial)'),
    r('OWASP Agentic', 'ASI04', 'OWASP Agentic ASI04 (partial)'),
    r('MITRE ATLAS', 'AML.T0011.002', 'MITRE ATLAS AML.T0011.002 (partial)'),
    r('MITRE ATLAS', 'AML.T0115.002', 'MITRE ATLAS AML.T0115.002 (partial)'),
    r('MITRE ATLAS', 'AML.T0110.000', 'MITRE ATLAS AML.T0110.000 (partial)'),
    r('NIST AI RMF', null, 'NIST AI RMF Measure'),
  ],
  r19: [
    r('OWASP LLM', 'LLM06'),
    r('OWASP Agentic', 'ASI09'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
  ],
  r20: [
    r('OWASP LLM', 'LLM01'),
    r('OWASP Agentic', 'ASI01'),
    r('MITRE ATLAS', 'AML.T0051', 'MITRE ATLAS AML.T0051 (partial)'),
    r('NIST AI RMF', null, 'NIST AI RMF Measure'),
    r('Goodside', null, 'Goodside 2024'),
    r('CVE', 'CVE-2024-5184'),
  ],
  r21: [
    r('OWASP LLM', 'LLM02'),
    r('OWASP LLM', 'LLM05'),
    r('OWASP LLM', 'LLM07'),
    r('MITRE ATLAS', 'AML.T0113', 'MITRE ATLAS AML.T0113 (partial)'),
  ],
  r22: [
    r('OWASP LLM', 'LLM02', 'OWASP LLM02 (partial)'),
    r('OWASP Agentic', 'ASI02', 'OWASP Agentic ASI02 (partial)'),
    r('MITRE ATLAS', 'AML.T0098', 'MITRE ATLAS AML.T0098 (partial)'),
    r('MITRE ATLAS', 'AML.T0113', 'MITRE ATLAS AML.T0113 (partial)'),
    r('MITRE ATLAS', 'AML.T0091.001', 'MITRE ATLAS AML.T0091.001 (partial)'),
    r('MITRE ATLAS', null, 'MITRE ATLAS Credential Access'),
    r('NIST AI RMF', null, 'NIST AI RMF Measure'),
  ],
  r23: [
    r('OWASP LLM', 'LLM06'),
    r('OWASP Agentic', 'ASI03'),
    r('MITRE ATLAS', 'AML.T0091.001', 'MITRE ATLAS AML.T0091.001 (partial)'),
    r('MITRE ATLAS', null, 'MITRE ATLAS Evasion'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
    r('AP2', '§6.2'),
  ],
  r24: [
    r('OWASP LLM', 'LLM10'),
    r('MITRE ATLAS', null, 'MITRE ATLAS Credential Access'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
    r('OWASP API', 'API2:2023'),
  ],
  r25: [
    r('OWASP LLM', 'LLM10'),
  ],
  r26: [
    r('OWASP LLM', 'LLM06'),
  ],
  r27: [
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
  ],
  r28: [
    r('OWASP LLM', 'LLM10'),
    r('OWASP Agentic', 'ASI08'),
    r('MITRE ATLAS', null, 'MITRE ATLAS Impact'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
  ],
  r29: [
    r('OWASP Agentic', 'ASI07', 'OWASP Agentic ASI07 (registered-agent delegation)'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
  ],
  r30: [
    r('OWASP MCP', 'MCP02'),
  ],
  r31: [
    r('OWASP LLM', 'LLM06'),
    r('OWASP LLM', 'LLM10'),
    r('OWASP Agentic', 'ASI08'),
    r('MITRE ATLAS', null, 'MITRE ATLAS Impact'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
    r('GDM', null, 'GDM AI Control Roadmap / TRAIT&R'),
  ],
  r32: [
    r('OWASP LLM', 'LLM02'),
    r('OWASP LLM', 'LLM06'),
    r('OWASP LLM', 'LLM10'),
    r('OWASP Agentic', 'ASI02'),
    r('OWASP Agentic', 'ASI08'),
    r('MITRE ATLAS', 'AML.T0139', 'MITRE ATLAS AML.T0139 (bounds the stolen-signing-key window)'),
    r('MITRE ATLAS', null, 'MITRE ATLAS Impact'),
    r('NIST AI RMF', null, 'NIST AI RMF Manage'),
  ],
  r_custom: [],
};

/**
 * Return the standards list for a rule. Unknown types fall through to
 * the empty list — the finder treats a rule with no standards as
 * "no standards match on this rule", NEVER as "match everything".
 */
export function standardsForRule(type: RuleType | string): readonly StandardRef[] {
  const row = (RULE_STANDARDS as Readonly<Record<string, readonly StandardRef[]>>)[type];
  return row ?? [];
}

/**
 * Family name a merchant sees on a picker row: every OWASP list reads
 * "OWASP", MITRE ATLAS reads "MITRE", NIST AI RMF reads "NIST". The
 * other frameworks are already one short word (AP2, CVE, FTC, …).
 */
export function standardFamily(framework: StandardFramework): string {
  if (framework.startsWith('OWASP')) return 'OWASP';
  if (framework === 'MITRE ATLAS') return 'MITRE';
  if (framework === 'NIST AI RMF') return 'NIST';
  return framework;
}

/** How many families a picker row shows before collapsing to "+N". */
export const PICKER_FAMILY_LIMIT = 3;

/**
 * One-line family summary for the Add-rule picker row, e.g.
 * "OWASP · MITRE · NIST +1". Families keep their declared order and
 * appear once each; past `limit` the rest collapse to "+N". Empty
 * string when the rule maps to nothing (r_custom), so the caller can
 * render the bare rule number.
 */
export function standardFamiliesSummary(
  type: RuleType | string,
  limit: number = PICKER_FAMILY_LIMIT,
): string {
  const families: string[] = [];
  for (const ref of standardsForRule(type)) {
    const family = standardFamily(ref.framework);
    if (!families.includes(family)) families.push(family);
  }
  if (families.length === 0) return '';
  const shown = families.slice(0, Math.max(1, limit));
  const rest = families.length - shown.length;
  return rest > 0 ? `${shown.join(' · ')} +${rest}` : shown.join(' · ');
}

export interface StandardGroup {
  readonly framework: StandardFramework;
  /** Ids in declared order. A tactic-only ref (id null) contributes the
   *  part of its label after the framework name ("Initial Access"). */
  readonly ids: readonly string[];
}

/**
 * The rule's standards grouped by framework, for the rule detail / edit
 * pane list: one line per framework, ids after it. Frameworks keep the
 * order of their first appearance.
 */
export function standardsByFramework(type: RuleType | string): readonly StandardGroup[] {
  const groups: { framework: StandardFramework; ids: string[] }[] = [];
  for (const ref of standardsForRule(type)) {
    const id =
      ref.id ??
      (ref.label.startsWith(`${ref.framework} `)
        ? ref.label.slice(ref.framework.length + 1)
        : ref.label);
    const existing = groups.find((g) => g.framework === ref.framework);
    if (existing) {
      if (!existing.ids.includes(id)) existing.ids.push(id);
    } else {
      groups.push({ framework: ref.framework, ids: [id] });
    }
  }
  return groups;
}

/**
 * Return the `StandardRef[]` entries on `type` that match `query`. The
 * query matches a standard when, case-insensitively:
 *   - it names the framework exactly ("mitre atlas", "owasp llm",
 *     "owasp agentic") — a multi-word alias from `FRAMEWORK_ALIASES`;
 *   - it names a framework alias ("mitre", "atlas", "owasp", "nist",
 *     "ap2", "cma", "dmcc") — a single-word alias;
 *   - it matches the standard id as a substring ("llm01", "LLM01",
 *     "T0139", "aml.t0139", "api4") — tolerates the `AML.` prefix the
 *     canon uses on ATLAS techniques.
 *
 * Returns the matched refs preserving declared order so a stable
 * "Matches: X, Y +N more" line can be rendered.
 */
export function matchStandardsOnRule(
  type: RuleType | string,
  query: string,
): readonly StandardRef[] {
  const trimmed = query.trim().toLowerCase();
  if (trimmed === '') return [];
  const refs = standardsForRule(type);
  if (refs.length === 0) return [];

  // Framework-alias match: "owasp" / "mitre" / "nist" / "ap2" / "cma" /
  // "dmcc" / "atlas". Multi-word aliases ("owasp llm", "mitre atlas") are
  // keys too; checked by exact-equality on the trimmed query.
  const aliasedFrameworks = FRAMEWORK_ALIASES[trimmed];
  if (aliasedFrameworks !== undefined) {
    const set = new Set<StandardFramework>(aliasedFrameworks);
    return refs.filter((rf) => set.has(rf.framework));
  }

  // Id-shaped match — substring check on the canonical id. Also match
  // against the stripped "AML." prefix so "T0139" lands on
  // "AML.T0139". Done per-ref so partial-only cells still match.
  const stripAml = (s: string): string => s.replace(/^aml\./i, '').toLowerCase();
  return refs.filter((rf) => {
    if (rf.id === null) return false;
    const idLower = rf.id.toLowerCase();
    if (idLower.includes(trimmed)) return true;
    if (stripAml(idLower).includes(stripAml(trimmed))) return true;
    return false;
  });
}

/** True iff some standard on `type` matches `query`. */
export function ruleHasStandardMatch(type: RuleType | string, query: string): boolean {
  return matchStandardsOnRule(type, query).length > 0;
}
